import type { ChatItem, ChatRequest, ChatResponse, ConversationSummary } from "../types";
import { getApiToken, NotAuthenticatedError } from "../auth/msal";

// Typed client for the backend. Attaches the Entra bearer token when this
// deployment requires one; locally, with auth off, it sends none.

/** Exported so the dashboard can reuse the exact same header logic. A second
 *  implementation would be the obvious place for one of them to forget the token —
 *  which works in local dev, where auth is off, and 401s for every real user. */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await getApiToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Attach the reporting-only turn context.
 *
 *  Added here, at the single outbound seam, rather than at each call site — a second
 *  call site that forgot it would silently produce turns labelled "other". It does
 *  not affect the answer, and the backend folds it into a closed set anyway. */
function withTurnContext<T extends object>(req: T): T & { clientPlatform: string } {
  const narrow = typeof matchMedia === "function" && matchMedia("(max-width: 768px)").matches;
  return { ...req, clientPlatform: narrow ? "web-mobile" : "web" };
}

/** Send a turn, optionally rendering the assistant's lead-in as soon as it arrives.
 *
 *  With `onReply`, the request asks for server-sent events and the backend emits the
 *  `reply` field the moment it closes in the model's JSON — typically seconds before the
 *  card. The turn takes exactly as long either way; it just stops being a blank spinner.
 *
 *  The FINAL object always comes from the `done` event, never assembled on the client, so
 *  a streamed turn renders identically to a non-streamed one. Any stream failure falls
 *  back to a normal POST rather than surfacing an error: the answer matters more than the
 *  progressive display. */
export async function sendChat(
  req: ChatRequest & { conversationId?: string | null },
  onReply?: (text: string) => void,
): Promise<ChatResponse> {
  if (onReply) {
    try {
      return await streamChat(req, onReply);
    } catch (err) {
      // An auth failure must NOT be retried as a plain POST — it would fail again and
      // the second failure would mask the real cause. Both flavours pass straight
      // through: one means "sign in again", the other "this account is not allowed".
      if (err instanceof NotAuthenticatedYet || err instanceof NotAuthenticatedError) throw err;
      console.warn("[chat] streaming failed, falling back to a plain request:", err);
    }
  }
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(withTurnContext(req)),
  });
  // 401/403 here means the token was missing or rejected, which is a completely
  // different thing to tell the user than "the backend broke": most often the
  // account is outside Crown's tenant, or admin consent has not been granted.
  //
  // Deliberately NOT classify(): on this route 503 is `auth_not_configured`, not
  // "no history store", so borrowing that mapping would hide the history UI over an
  // unrelated backend misconfiguration.
  if (res.status === 401 || res.status === 403) throw new NotAuthenticatedYet(`chat auth failed (${res.status})`);
  if (!res.ok) throw new Error(`chat failed: ${res.status}`);
  return (await res.json()) as ChatResponse;
}

/** SSE transport for sendChat. Kept separate so the plain path stays exactly as it was. */
async function streamChat(
  req: ChatRequest & { conversationId?: string | null },
  onReply: (text: string) => void,
): Promise<ChatResponse> {
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { ...(await authHeaders()), Accept: "text/event-stream" },
    body: JSON.stringify(withTurnContext(req)),
  });
  if (res.status === 401 || res.status === 403) throw new NotAuthenticatedYet(`chat auth failed (${res.status})`);
  if (!res.ok) throw new Error(`chat failed: ${res.status}`);
  if (!res.body) throw new Error("chat stream had no body");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: ChatResponse | null = null;

  for (;;) {
    const { value, done: finished } = await reader.read();
    if (value) buf += decoder.decode(value, { stream: true });
    // Events are separated by a blank line; a partial event stays in the buffer.
    let sep: number;
    while ((sep = buf.indexOf("\n\n")) !== -1) {
      const raw = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let event = "message";
      let data = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (!data) continue;
      const parsed = JSON.parse(data) as Record<string, unknown>;
      if (event === "reply" && typeof parsed.text === "string") onReply(parsed.text);
      else if (event === "done") done = parsed as unknown as ChatResponse;
      else if (event === "error") throw new Error(`chat failed: ${String(parsed.error ?? "stream error")}`);
    }
    if (finished) break;
  }
  // A stream that ends without `done` is a truncated turn, not an empty answer — say so
  // rather than rendering a blank card.
  if (!done) throw new Error("chat stream ended before the response arrived");
  return done;
}

/** Thrown when the backend has no conversation store configured.
 *  Distinct from a real failure so the pane can hide the history UI entirely
 *  rather than showing an empty list, which would read as "you have no chats". */
export class HistoryUnavailable extends Error {}

/** Thrown when the BACKEND refused the token we sent (401/403).
 *
 *  Distinct from NotAuthenticatedError, which means the browser could not obtain a
 *  token in the first place. The two need different messages: one is "sign in
 *  again", the other is "you signed in, but this account is not allowed here" —
 *  usually a user in the wrong tenant, or consent not yet granted. Collapsing them
 *  sends someone round a sign-in loop that cannot succeed. */
export class NotAuthenticatedYet extends Error {}

function classify(status: number): Error | null {
  if (status === 503) return new HistoryUnavailable("history_unavailable");
  if (status === 401 || status === 403) return new NotAuthenticatedYet(`auth not ready (${status})`);
  return null;
}

export async function listConversations(): Promise<ConversationSummary[]> {
  const res = await fetch("/api/conversations", { headers: await authHeaders() });
  const known = classify(res.status);
  if (known) throw known;
  if (!res.ok) throw new Error(`conversations failed: ${res.status}`);
  return ((await res.json()) as { conversations: ConversationSummary[] }).conversations;
}

export async function loadConversation(
  id: string,
): Promise<{ summary: ConversationSummary; messages: { seq: number; role: string; body: ChatItem | null }[] }> {
  const res = await fetch(`/api/conversations/${encodeURIComponent(id)}`, { headers: await authHeaders() });
  const known = classify(res.status);
  if (known) throw known;
  if (!res.ok) throw new Error(`conversation failed: ${res.status}`);
  return await res.json();
}

/** Soft delete — hides the conversation. The analytics row is kept by design, so
 *  don't describe this to the user as erasing everything. */
export async function deleteConversation(id: string): Promise<void> {
  const res = await fetch(`/api/conversations/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`delete failed: ${res.status}`);
}

/** Record a thumbs rating on one turn.
 *
 *  Deliberately FIRE-AND-FORGET at the UI layer: the pane flips the thumb
 *  optimistically and this reports success only so a failure can be logged. A
 *  rating that fails to save is not worth interrupting the user for — but silently
 *  swallowing it would hide a broken endpoint forever, so it is logged.
 *
 *  `feedback: null` clears a rating, which is what makes a mis-click undoable. */
/** `comment` OMITTED and `comment: null` mean different things and both are used: the
 *  thumb is written the instant it is clicked, and the comment only if the user then
 *  types one. Omitting it leaves any stored comment alone; null clears it. Sending
 *  `comment: undefined` would serialise away to the same thing as omitting, which is
 *  why the key is added conditionally rather than always. */
export async function sendFeedback(usageEventId: number, feedback: "up" | "down" | null, comment?: string | null): Promise<boolean> {
  try {
    const payload: Record<string, unknown> = { usageEventId, feedback };
    if (comment !== undefined) payload.comment = comment;
    const res = await fetch("/api/feedback", {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) console.warn(`[feedback] not saved (${res.status})`);
    return res.ok;
  } catch (err) {
    console.warn("[feedback] not saved:", err);
    return false;
  }
}

/** Accept a fact the user supplied that Crown's documents do not contain.
 *
 *  UNLIKE sendFeedback, THIS ONE REPORTS FAILURE TO THE CALLER and the caller acts on
 *  it. A lost thumbs rating costs a data point; a lost addition means the next turn
 *  answers as though the user never added anything, after they watched themselves
 *  confirm it. Silently swallowing that would make the feature look broken at random.
 *
 *  The TEXT is sent rather than an id because nothing was stored when the card was
 *  built — the card carries the model's proposal, and this call is what makes it real.
 */
export async function acceptAddition(conversationId: string, text: string): Promise<boolean> {
  try {
    const res = await fetch("/api/chat/additions", {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ conversationId, text }),
    });
    if (!res.ok) console.warn(`[additions] not saved (${res.status})`);
    return res.ok;
  } catch (err) {
    console.warn("[additions] not saved:", err);
    return false;
  }
}
